import { ElementRefer } from '@src/slide/application/service/snap-line-service/context/refer/base';
import {
    getFrameAllGrids,
    isCompleteCoveredBy,
} from '@src/slide/application/service/snap-line-service/util/frame';
import { RefSourceType } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import type { IRefElement } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import type { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import type { ISnapGridLine } from '@src/slide/application/service/snap-line-service/interface/line.interface';

/**
 * 动态的参照物选取规则
 * 即元素对应的参照物不固定，根据元素的位置和其他因素综合判定得出
 */
export class DynamicRefer extends ElementRefer {
    /**
     * 获取投影有交集的元素
     */
    public setRefWithShadowOverlappedElements(detectFrame: IFrame) {
        const staticRefs = this.context.staticRefer.refs;

        const detectRef = { outerFrameOnSlide: detectFrame };
        const overlappedElements = staticRefs
            // 非Slide本身，并且有在x/y方向上的投影重叠才会被视为参照元素
            .filter(ref => ref.source !== RefSourceType.SLIDE && this.isShadowOverlapped(
                detectRef as IRefElement,
                ref,
            ))
            // 过滤完全把 detectElement 覆盖的元素，不作为动态refer，比如背景图片
            .filter((ref) => {
                const referFrame = ref.outerFrameOnSlide;
                const { detectFrame } = this.context;

                return !isCompleteCoveredBy(detectFrame, referFrame);
            });

        this.setRefs(overlappedElements);
    }

    // 动态的不需要提前去根据 ref计算 grid。大部分都有自己的规则，这个交给 detector去自行计算
    public gridifyElements(elements: IRefElement[]): ISnapGridLine[] {
        return [];
    }

    /**
     * 网格化
     * 一个参照元素，将按其矩形的rect，生成在2个方向，按边缘生成4条网格线用于后续判定
     * 相比static, 少了2条基线的grid
     */
    public gridify(frame: IFrame, ref: IRefElement): ISnapGridLine[] {
        const {
            topGrid, bottomGrid, leftGrid, rightGrid,
        } = getFrameAllGrids(frame);

        return [
            topGrid,
            bottomGrid,
            leftGrid,
            rightGrid,
        ].map(({ orient, position, type }) => ({
            orient,
            position,
            type,
            maxAdsorbGap: this.context.option.maxAdsorbGap,
            source: ref.source,
            refElements: [ref],
        }));
    }

    /**
     * 是否有投影交集
     */
    public isShadowOverlapped(src: IRefElement, ref: IRefElement): boolean {
        const srcFrame = src.outerFrameOnSlide;
        const refFrame = ref.outerFrameOnSlide;

        const isVerticalOverlapped = srcFrame.left < (refFrame.left + refFrame.width)
            && (srcFrame.left + srcFrame.width) > refFrame.left;
        const isHorizontalOverlapped = srcFrame.top < (refFrame.top + refFrame.height)
            && (srcFrame.top + srcFrame.height) > refFrame.top;

        return isHorizontalOverlapped || isVerticalOverlapped;
    }

    /**
     * 获取主要元素
     * 过滤掉被其他元素覆盖的元素
     */
    private filterCoveredElements(elements: IRefElement[]): IRefElement[] {
        const frames = elements.map(ele => ({
            ...ele.outerFrameOnSlide,
            ele,
        }));
        const sortedFrames = this.sortByAreaDescending(frames);
        const result: IRefElement[] = [];

        for (let i = 0; i < sortedFrames.length; i++) {
            let isCoveredFlag = false;

            for (let j = 0; j < i; j++) {
                if (this.isCovered(sortedFrames[i], sortedFrames[j])) {
                    isCoveredFlag = true;
                    break;
                }
            }

            if (!isCoveredFlag) {
                result.push((sortedFrames[i] as any).ele);
            }
        }

        return result;
    }

    /**
     * 判定 srcFrame 是否覆盖了 targetFrame
     * 如果覆盖面积超过 95%，则判定为覆盖
     */
    private isCovered(srcFrame: IFrame, targetFrame: IFrame, coverageThreshold = 0.9): boolean {
        const left = Math.max(srcFrame.left, targetFrame.left);
        const right = Math.min(srcFrame.left + srcFrame.width, targetFrame.left + targetFrame.width);
        const top = Math.max(srcFrame.top, targetFrame.top);
        const bottom = Math.min(srcFrame.top + srcFrame.height, targetFrame.top + targetFrame.height);

        const intersectionWidth = Math.max(0, right - left);
        const intersectionHeight = Math.max(0, bottom - top);
        const intersectionArea = intersectionWidth * intersectionHeight;

        const srcFrameArea = srcFrame.width * srcFrame.height;
        const coverage = intersectionArea / srcFrameArea;

        return coverage >= coverageThreshold;
    }

    /**
     * 按面积排序
     */
    private sortByAreaDescending(frames: IFrame[]): IFrame[] {
        return frames.sort((a, b) => (b.width * b.height) - (a.width * a.height));
    }
}
